import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Grid,
  CircularProgress,
  Checkbox,
} from '@mui/material'
import React, { useEffect, useState } from 'react'

import { OpportunityScanResponse } from '../types/reverseScan'

interface OrgMatchWithOptions extends OpportunityScanResponse {
  expanded: boolean
  selected: boolean
}

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { useCustomMutation, useNotification } from '@refinedev/core'

const MATCH_QUALITY_COLORS = [
  '#f44336',
  '#ff9800',
  '#ffeb3b',
  '#4caf50',
  '#2196f3',
]

const getMatchQualityColor = (rank: number) => {
  return MATCH_QUALITY_COLORS[rank - 1] || '#9e9e9e'
}

const getMatchQualityLabel = (rank: number) => {
  const labels = ['Very Poor', 'Poor', 'Average', 'Good', 'Excellent']
  return labels[rank - 1] || 'Unknown'
}

interface ReverseScanResultsDialogProps {
  showScanResults: boolean
  setShowScanResults: (showScanResults: boolean) => void
  scanResults: OpportunityScanResponse[]
  oppName: string | null
  funderName: string
}

export default function ReverseScanResultsDialog({
  showScanResults,
  setShowScanResults,
  scanResults,
  oppName,
  funderName,
}: ReverseScanResultsDialogProps) {
  const [orgMatches, setOrgMatches] = useState<OrgMatchWithOptions[]>([])
  const [lowerRankedMatches, setLowerRankedMatches] = useState<
    OrgMatchWithOptions[]
  >([])
  const [notifying, setNotifying] = useState(false)
  const [notified, setNotified] = useState(false)
  const [showLowerRanked, setShowLowerRanked] = useState(false)

  useEffect(() => {
    if (scanResults.length > 0) {
      const highRankedResults: OrgMatchWithOptions[] = []
      const lowerRankedResults: OrgMatchWithOptions[] = []

      scanResults.forEach((result) => {
        const matchWithOptions: OrgMatchWithOptions = {
          ...result,
          expanded: false,
          selected: false,
        }
        if (result.result_rank > 3) {
          highRankedResults.push(matchWithOptions)
        } else {
          lowerRankedResults.push(matchWithOptions)
        }
      })

      setOrgMatches(highRankedResults)
      setLowerRankedMatches(lowerRankedResults)
    }
  }, [scanResults])

  const toggleMatchExpansion = (matchId: number) => {
    setOrgMatches((prev) =>
      prev.map((match) => ({
        ...match,
        expanded: match.id === matchId ? !match.expanded : match.expanded,
      }))
    )
  }

  const toggleLowerMatchExpansion = (matchId: number) => {
    setLowerRankedMatches((prev) =>
      prev.map((match) => ({
        ...match,
        expanded: match.id === matchId ? !match.expanded : match.expanded,
      }))
    )
  }

  const toggleMatchSelection = (matchId: number) => {
    setOrgMatches((prev) =>
      prev.map((match) => ({
        ...match,
        selected: match.id === matchId ? !match.selected : match.selected,
      }))
    )
  }

  const toggleSelectAll = () => {
    const allSelected = orgMatches.every((match) => match.selected)
    setOrgMatches((prev) =>
      prev.map((match) => ({
        ...match,
        selected: !allSelected,
      }))
    )
  }

  const { mutate: notifyOrgs } = useCustomMutation()
  const { open: openNotification } = useNotification()

  function handleNotifyOrgs() {
    setNotifying(true)
    setNotified(false)

    notifyOrgs(
      {
        url: `/orgs/notify-match`,
        method: 'post',
        values: {
          orgs: orgMatches
            .filter((match) => match.selected)
            .map(({ expanded, selected, ...match }) => match), // remove expanded and selected from obj
        },
      },
      {
        onSuccess: async () => {
          openNotification?.({
            message: 'Orgs notified successfully',
            type: 'success',
          })
          setNotifying(false)
          setNotified(true)
        },
        onError: () => {
          openNotification?.({
            message: 'Failed to notify orgs',
            type: 'error',
          })
          setNotifying(false)
        },
      }
    )
  }

  const selectedCount = orgMatches.filter((match) => match.selected).length

  const handleClose = () => {
    setShowScanResults(false)
    setLowerRankedMatches([])
    setNotified(false)
    setShowLowerRanked(false)
  }

  return (
    <Dialog
      open={showScanResults}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            Match Results
            <Typography variant="subtitle1" color="text.secondary">
              Organisations matching: {oppName || '*No fund name provided*'} -{' '}
              {funderName}
            </Typography>
          </Box>
          <Typography variant="subtitle2" color="text.secondary">
            {orgMatches.length} potential matches
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {orgMatches.length > 0 ? (
          <>
            <Box sx={{ mb: 2, p: 1, bgcolor: '#f8f9fa', borderRadius: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Showing organisations that would be strong matches for this
                  funding opportunity.
                </Typography>
                <Button
                  variant="contained"
                  color={notified ? 'success' : 'primary'}
                  disabled={selectedCount === 0 || notifying || notified}
                  onClick={handleNotifyOrgs}
                  sx={{ ml: 6, textTransform: 'none', width: '250px' }}
                >
                  {notifying ? (
                    <>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      Notifying...
                    </>
                  ) : notified ? (
                    <>
                      <CheckCircleOutlineIcon sx={{ mr: 1 }} />
                      Notified
                    </>
                  ) : (
                    `Notify Selected Orgs (${selectedCount})`
                  )}
                </Button>
              </Box>
            </Box>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={
                          orgMatches.length > 0 &&
                          orgMatches.every((match) => match.selected)
                        }
                        indeterminate={
                          selectedCount > 0 && selectedCount < orgMatches.length
                        }
                        onChange={toggleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Organisation</TableCell>
                    <TableCell>Funding Need</TableCell>
                    <TableCell align="center">Match Score</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orgMatches.map((match) => (
                    <React.Fragment key={match.id}>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={match.selected}
                            onChange={() => toggleMatchSelection(match.id)}
                          />
                        </TableCell>
                        <TableCell>{match.org_name}</TableCell>
                        <TableCell>{match.funding_need_title}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${
                              match.result_rank
                            } - ${getMatchQualityLabel(match.result_rank)}`}
                            style={{
                              backgroundColor: getMatchQualityColor(
                                match.result_rank
                              ),
                              color: match.result_rank <= 2 ? 'white' : 'black',
                              minWidth: '120px',
                            }}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => toggleMatchExpansion(match.id)}
                          >
                            {match.expanded ? 'Hide Details' : 'Show Details'}
                          </Button>
                        </TableCell>
                      </TableRow>

                      {match.expanded && (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            sx={{ p: 3, backgroundColor: '#f5f5f5' }}
                          >
                            <Grid container spacing={3}>
                              <Grid item xs={12}>
                                <Box>
                                  <Typography variant="subtitle1" gutterBottom>
                                    Match Result:
                                  </Typography>
                                  <Typography variant="body2" paragraph>
                                    {match.result_value}
                                  </Typography>
                                  <Typography variant="subtitle1" gutterBottom>
                                    Follow up Questions:
                                  </Typography>
                                  <Box
                                    component="ul"
                                    sx={{ margin: 0, paddingLeft: 2 }}
                                  >
                                    {match.questions ? (
                                      match.questions.split('\n').map(
                                        (question, idx) =>
                                          question.trim() && (
                                            <Box
                                              component="li"
                                              key={idx}
                                              sx={{ mb: 0.5 }}
                                            >
                                              <Typography variant="body2">
                                                {question.trim()}
                                              </Typography>
                                            </Box>
                                          )
                                      )
                                    ) : (
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        No follow-up questions
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <Box>
                                  <Typography variant="subtitle1" gutterBottom>
                                    Organisation Profile:
                                  </Typography>
                                  <Box
                                    sx={{
                                      height: '450px',
                                      overflow: 'auto',
                                      p: 2,
                                      border: '1px solid #e0e0e0',
                                      borderRadius: 1,
                                      backgroundColor: '#fff',
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{ whiteSpace: 'pre-wrap' }}
                                    >
                                      {match.org_profile}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <Box>
                                  <Typography variant="subtitle1" gutterBottom>
                                    Funding Need Locations:
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      gap: 0.5,
                                      flexWrap: 'wrap',
                                      mb: 2,
                                    }}
                                  >
                                    {match.funding_need_regions.map(
                                      (region, idx) => (
                                        <Chip
                                          key={idx}
                                          label={region}
                                          size="small"
                                          color="primary"
                                          variant="outlined"
                                        />
                                      )
                                    )}
                                  </Box>

                                  <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                                    <Box>
                                      <Typography
                                        variant="subtitle1"
                                        gutterBottom
                                      >
                                        Amount Needed:
                                      </Typography>
                                      <Typography variant="body2">
                                        {match.amount_str}
                                      </Typography>
                                    </Box>
                                  </Box>

                                  <Typography variant="subtitle1" gutterBottom>
                                    Funding Need Description:
                                  </Typography>
                                  <Box
                                    sx={{
                                      height: '305px',
                                      overflow: 'auto',
                                      p: 2,
                                      border: '1px solid #e0e0e0',
                                      borderRadius: 1,
                                      backgroundColor: '#fff',
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{ whiteSpace: 'pre-wrap' }}
                                    >
                                      {match.request_description}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                            </Grid>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Lower Ranked Matches Section */}
            {lowerRankedMatches.length > 0 && (
              <>
                <Box sx={{ mt: 4, mb: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setShowLowerRanked(!showLowerRanked)}
                    sx={{ mb: 2 }}
                  >
                    {showLowerRanked ? 'Hide' : 'Show'} Lower Ranked Matches (
                    {lowerRankedMatches.length})
                  </Button>

                  {showLowerRanked && (
                    <Box sx={{ p: 1, bgcolor: '#fff3e0', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Lower Ranked Matches</strong> - Organisations
                        with lower ranked matches (ranks 1-3). These
                        organisations cannot be notified.
                      </Typography>
                    </Box>
                  )}
                </Box>

                {showLowerRanked && (
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Organisation</TableCell>
                          <TableCell>Funding Need</TableCell>
                          <TableCell align="center">Match Score</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {lowerRankedMatches.map((match) => (
                          <React.Fragment key={match.id}>
                            <TableRow>
                              <TableCell>{match.org_name}</TableCell>
                              <TableCell>{match.funding_need_title}</TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={`${
                                    match.result_rank
                                  } - ${getMatchQualityLabel(
                                    match.result_rank
                                  )}`}
                                  style={{
                                    backgroundColor: getMatchQualityColor(
                                      match.result_rank
                                    ),
                                    color:
                                      match.result_rank <= 2
                                        ? 'white'
                                        : 'black',
                                    minWidth: '120px',
                                  }}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() =>
                                    toggleLowerMatchExpansion(match.id)
                                  }
                                >
                                  {match.expanded
                                    ? 'Hide Details'
                                    : 'Show Details'}
                                </Button>
                              </TableCell>
                            </TableRow>

                            {match.expanded && (
                              <TableRow>
                                <TableCell
                                  colSpan={4}
                                  sx={{ p: 3, backgroundColor: '#f5f5f5' }}
                                >
                                  <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                      <Box>
                                        <Typography
                                          variant="subtitle1"
                                          gutterBottom
                                        >
                                          Match Result:
                                        </Typography>
                                        <Typography variant="body2" paragraph>
                                          {match.result_value}
                                        </Typography>
                                      </Box>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                      <Box>
                                        <Typography
                                          variant="subtitle1"
                                          gutterBottom
                                        >
                                          Organisation Profile:
                                        </Typography>
                                        <Box
                                          sx={{
                                            height: '450px',
                                            overflow: 'auto',
                                            p: 2,
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 1,
                                            backgroundColor: '#fff',
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            sx={{ whiteSpace: 'pre-wrap' }}
                                          >
                                            {match.org_profile}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                      <Box>
                                        <Typography
                                          variant="subtitle1"
                                          gutterBottom
                                        >
                                          Funding Need Locations:
                                        </Typography>
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            gap: 0.5,
                                            flexWrap: 'wrap',
                                            mb: 2,
                                          }}
                                        >
                                          {match.funding_need_regions.map(
                                            (region, idx) => (
                                              <Chip
                                                key={idx}
                                                label={region}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                              />
                                            )
                                          )}
                                        </Box>

                                        <Box
                                          sx={{
                                            display: 'flex',
                                            gap: 3,
                                            mb: 2,
                                          }}
                                        >
                                          <Box>
                                            <Typography
                                              variant="subtitle1"
                                              gutterBottom
                                            >
                                              Amount Needed:
                                            </Typography>
                                            <Typography variant="body2">
                                              {match.amount_str}
                                            </Typography>
                                          </Box>
                                        </Box>

                                        <Typography
                                          variant="subtitle1"
                                          gutterBottom
                                        >
                                          Funding Need Description:
                                        </Typography>
                                        <Box
                                          sx={{
                                            height: '305px',
                                            overflow: 'auto',
                                            p: 2,
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 1,
                                            backgroundColor: '#fff',
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            sx={{ whiteSpace: 'pre-wrap' }}
                                          >
                                            {match.request_description}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </>
        ) : (
          <Typography
            variant="body1"
            color="textSecondary"
            align="center"
            sx={{ mt: 2 }}
          >
            No organisation matches found
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
